import { defineComponent, ref, Ref, onMounted, watch } from 'vue';
import { HoursExceptionModel, HoursOfOperationDisplayType, HoursEntryModel, HoursOfOperationType, EntryModel } from '@truckdown/locations';
import { formatDate, formatDateRange, deepCloneWithDates, showMessage } from '../../../utilities';
import validationError from '../../../Validation/Error/Error.vue';
import { ExceptionValidator } from '../Validation/ExceptionValidator';
interface TempExceptionModel extends HoursExceptionModel {
    dateRange?: [Date, Date]; 
}

interface IData {
    exceptionModel: Ref<TempExceptionModel>,
    allExceptions: Ref<HoursExceptionModel[]>,
    tempExceptions: Ref<TempExceptionModel[]>,
    showDeleteExceptionModal: Ref<boolean>,
    deleteIndex: Ref<number | null>
    isEditing: Ref<boolean[]>,
    exceptionValidators: Ref<{ [key: string]: ExceptionValidator }>
}

const getInitialData = function (props: any): IData {
    let exception = {
        id: "",
        name: "",
        locationIds: [],
        date: new Date(),
        enabled: true,
        type: HoursOfOperationType.AlwaysOpen,
        entries: {
            afterHours24: false,
            enabled: false,
            entries: []
        } as HoursEntryModel,
        dateRange: undefined
    }
    return {
        allExceptions: ref<HoursExceptionModel[]>(props.exceptions || []),
        tempExceptions: ref<HoursExceptionModel[]>(toTempExceptionModels(props.exceptions) || []),
        exceptionModel: ref<TempExceptionModel>(exception),
        showDeleteExceptionModal: ref<boolean>(false),
        deleteIndex: ref<number | null>(null),
        isEditing: ref<boolean[]>(props.exceptions.map(() => false)),
        exceptionValidators: ref<{ [key: string]: ExceptionValidator }>({})
    };
}

const rebuildExceptionValidators = function (data: IData, props: any): boolean {
    data.exceptionValidators.value = {};
    let allValid = true;
    for (const e in data.tempExceptions.value) {
        const exception = data.tempExceptions.value[e];

        // 1. Exception-level validator (name, date, etc.)
        const exceptionKey = `${e}`;
        const exceptionValidator = new ExceptionValidator(exception, Number(e));
        if (!exceptionValidator.validate()) {
            allValid = false;
        }
        data.exceptionValidators.value[exceptionKey] = exceptionValidator;

        // 2. Entry-level validators
        const entries = exception.entries.entries;
        entries.forEach((entry, i) => {
            const entryKey = `${e}-${i}`;
            const validator = new ExceptionValidator(exception, Number(e), i);
            if (!validator.validate()) {
                allValid = false;
            }
            data.exceptionValidators.value[entryKey] = validator;
        });
    }

    
    if (!validateNewExceptionModel(data)) {
        if (props.addException) {
            allValid = false;
        }
    }

    return allValid;
};

const validateNewExceptionModel  = function (data: IData) {
    const newException = data.exceptionModel.value;
    let allValid = true;
    // 1. Exception-level validator
    const exceptionKey = `new`;
    const exceptionValidator = new ExceptionValidator(newException, "new");
    if (!exceptionValidator.validate()) {
        allValid = false;
    }
    data.exceptionValidators.value[exceptionKey] = exceptionValidator;

    // 2. Entry-level validators
    const entries = newException.entries.entries;
    entries.forEach((entry, i) => {
        const entryKey = `new-${i}`;
        const validator = new ExceptionValidator(newException, "new", i);
        if (!validator.validate()) {
            allValid = false;
        }
        data.exceptionValidators.value[entryKey] = validator;
    });

    return allValid;
}


function toTempExceptionModels(apiExceptions: HoursExceptionModel[]): TempExceptionModel[] {
    return apiExceptions.map(e => {
        const cloned = deepCloneWithDates(e);
        return {
            ...cloned,
            dateRange: [cloned.date as Date, cloned.date as Date] // start and end are same for single-date exception
        };
    });
}

function toApiExceptionModels(tempExceptions: TempExceptionModel[]): HoursExceptionModel[] {
    return tempExceptions.flatMap(splitExceptionDateRange);
}

function splitExceptionDateRange(exception: TempExceptionModel): HoursExceptionModel[] {
    if (!exception.dateRange) {
        return [deepCloneWithDates(exception)]; // no splitting
    }

    const [start, end] = exception.dateRange;
    const results: HoursExceptionModel[] = [];

    // If end is null/undefined, use only the start date
    if (!end) {
        const singleException = {
            ...deepCloneWithDates(exception),
            date: new Date(start)
        };
        delete (singleException as any).dateRange;
        return [singleException];
    }

    const current = new Date(start);
    while (current <= end) {
        const newException = {
            ...deepCloneWithDates(exception),
            date: new Date(current)
        };
        delete (newException as any).dateRange;
        results.push(newException);
        current.setDate(current.getDate() + 1);
    }

    return results;
}


const getMethods = function (data: IData, ctx: any, props: any) {
    const validateEntriesByAllIndexes = function (exceptionIndex: number | "new", entryIndex: number) {
        return data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.validate(`startTime-${exceptionIndex}-${entryIndex}`) &&
        data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.validate(`overlap-${exceptionIndex}-${entryIndex}`) &&
        data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.validate(`range-${exceptionIndex}-${entryIndex}`) &&
        data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.validate(`endTime-${exceptionIndex}-${entryIndex}`);
    }

    const validateExceptionByIndex = function (exceptionIndex: number) {
        return data.tempExceptions.value[exceptionIndex].entries.entries.every((_, i) => validateEntriesByAllIndexes(exceptionIndex, i)) && 
            data.exceptionValidators.value[exceptionIndex].validate();
    }

    return {
        //removeException: function (indexToRemove: number) {
        //    if (indexToRemove > -1 && indexToRemove < data.tempExceptions.value.length) {
        //        data.tempExceptions.value.splice(indexToRemove, 1);
        //        rebuildExceptionValidators(data, props);
        //        ctx.emit("update-exceptions", toApiExceptionModels(data.tempExceptions.value), true);
        //    }
        //},
        displayHoursType: function (type: HoursOfOperationType) {
            switch (type) {
                case HoursOfOperationType.AlwaysOpen:
                    return "Always Open";
                case HoursOfOperationType.SelectHours:
                    return "Select Hours";
                case HoursOfOperationType.Closed:
                    return "Closed";
                case HoursOfOperationType.Unknown:
                    return "Unknown";
                default: return HoursOfOperationType[type];
            };
        },
        displayEntryHoursType: function (type: HoursOfOperationDisplayType) {
            switch (type) {
                case HoursOfOperationDisplayType.AfterHours:
                    return "After Hours";
                default: return HoursOfOperationDisplayType[type];
            };
        },
        changeNewExceptionHoursType: function () {
            if (data.exceptionModel.value.type === HoursOfOperationType.SelectHours && data.exceptionModel.value.entries.entries.length == 0) {
                let entryToAdd = {
                    startTime: "00:00",
                    endTime: "00:00",
                    type: HoursOfOperationDisplayType.Open
                }
                data.exceptionModel.value.entries.entries.push(entryToAdd);
                validateNewExceptionModel(data);
            }
            
        },
        changeExceptionHoursTypeEdit: function (index: number) {
            data.exceptionValidators.value[index].validate(`entries-${index}`);
            data.exceptionValidators.value[index].validate(`type-${index}`);
        },
        confirmExceptionAdd: function () {
            if (validateNewExceptionModel(data)) {
               data.tempExceptions.value.push(data.exceptionModel.value);
                data.exceptionModel.value = {
                    id: "",
                    name: "",
                    locationIds: [],
                    date: new Date(),
                    enabled: false,
                    type: HoursOfOperationType.AlwaysOpen,
                    entries: {
                        afterHours24: false,
                        enabled: false,
                        entries: []
                    } as HoursEntryModel,
                    dateRange: undefined
                }
                rebuildExceptionValidators(data, props); 
                ctx.emit("update-exceptions", toApiExceptionModels(data.tempExceptions.value), true);
            } 
        }, 
        cancelExceptionAdd: function () {
            ctx.emit("cancel-update");
        },
      
        deleteNewEntry: function (entryIndex: number) {
             data.exceptionModel.value.entries.entries.splice(entryIndex, 1);
             validateNewExceptionModel(data);
        },
        addEmptyEntry: function (index: number) {  
            let lastItem = data.tempExceptions.value[index].entries.entries[data.tempExceptions.value[index].entries.entries.length - 1];
            let typeToAdd = lastItem != undefined && lastItem.type === HoursOfOperationDisplayType.Open
                ? HoursOfOperationDisplayType.AfterHours
                : HoursOfOperationDisplayType.Open;
            data.tempExceptions.value[index].entries.entries.push({
                startTime: lastItem ? lastItem.endTime : "",
                endTime: "",
                type: typeToAdd
            }) 
            rebuildExceptionValidators(data, props); 
        },
    
        addNewExceptionEntry: function () {
            data.exceptionModel.value.entries.entries.push({
                startTime: "00:00",
                endTime: "00:00",
                type: HoursOfOperationDisplayType.Open
            });
            validateNewExceptionModel(data);
        },
        toggleEditException: function (index: number) {
            data.isEditing.value[index] = true;
        },
        deleteException: function (index: number) {
            data.deleteIndex.value = index;
            data.showDeleteExceptionModal.value = true;
        },
        confirmDeleteException: function () {
            if (data.deleteIndex.value || data.deleteIndex.value == 0)
            data.tempExceptions.value.splice(data.deleteIndex.value, 1);
            data.deleteIndex.value = null;
            data.showDeleteExceptionModal.value = false;
            rebuildExceptionValidators(data, props); 
            ctx.emit("update-exceptions", toApiExceptionModels(data.tempExceptions.value), true);
        },
        closeDeleteModal: function () {
            data.deleteIndex.value = null;
            data.showDeleteExceptionModal.value = false;
        },
        deleteEntry: function (outerIndex: number, innerIndex: number) {
            data.tempExceptions.value[outerIndex].entries.entries.splice(innerIndex, 1);
            rebuildExceptionValidators(data, props); 
        },
        removeNewExceptionEntry: function (index: number) {
            data.exceptionModel.value.entries.entries.splice(index, 1);
            validateNewExceptionModel(data);
        },
        finishEdit: function (index: number) {
            if (validateExceptionByIndex(index)) {
                data.isEditing.value[index] = false;
                ctx.emit("update-exceptions", toApiExceptionModels(data.tempExceptions.value), true);
            }
        },
        updateEnabled: function (index: number) {
            ctx.emit("update-exceptions", toApiExceptionModels(data.tempExceptions.value), true);
        },
     
        hasErrors: function (index: number) {
            const errors = data.exceptionValidators.value[index].errors;
            return !!(
                errors[`name-${index}`] ||
                errors[`date-${index}`] ||
                errors[`type-${index}`] ||
                errors[`entries-${index}`]
            );
        },
        editTime: function (exceptionIndex: number | 'new', entryIndex: number) {
            let entries;
            if (exceptionIndex == 'new') {
                entries = data.exceptionModel.value.entries.entries as EntryModel[];
            } else {
                 entries = data.tempExceptions.value[exceptionIndex].entries.entries as EntryModel[];
            }
            return entries.every((_, i) => validateEntriesByAllIndexes(exceptionIndex, i));
        },
        hasEndTimeError: function (exceptionIndex: number | 'new', entryIndex: number) {
            return data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.errors[`overlap-${exceptionIndex}-${entryIndex}`] ||
                data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.errors[`range-${exceptionIndex}-${entryIndex}`] ||
                data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.errors[`endTime-${exceptionIndex}-${entryIndex}`]
        },
        hasStartTimeError: function (exceptionIndex: number | 'new', entryIndex: number) {
            return data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.errors[`overlap-${exceptionIndex}-${entryIndex}`] ||
                data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.errors[`range-${exceptionIndex}-${entryIndex}`] ||
                data.exceptionValidators.value[`${exceptionIndex}-${entryIndex}`]?.errors[`startTime-${exceptionIndex}-${entryIndex}`]
        }, 
        getUpdatedExceptions: function () {
            if (rebuildExceptionValidators(data, props)) {
                return toApiExceptionModels(data.tempExceptions.value)
            } else {
                return [];
            }
            
        }
    };
}

export default defineComponent({
    name: "HoursExceptions",
    components: {
        'error': validationError
    },
    props: {
        exceptions: {
            type: Array as () => HoursExceptionModel[],
            required: true,
            default: []
        },
        addException: Boolean
    },
    emits: ['update-exceptions', 'cancel-update'],
    setup: function (props, ctx) {
        var data = getInitialData(props);
        var methods = getMethods(data, ctx, props);

        onMounted(() => {
            rebuildExceptionValidators(data, props);
        });

        ctx.expose({
            getExceptions: methods.getUpdatedExceptions
        })

        watch(
            () => [
                props.exceptions,
            ],
            ([newEdit, newOriginal]) => {
                const newVal = newEdit ?? newOriginal ?? undefined;
                if (newVal != undefined) {
                    data.allExceptions.value = JSON.parse(JSON.stringify(props.exceptions));
                    data.tempExceptions.value = toTempExceptionModels(JSON.parse(JSON.stringify(props.exceptions)));
                    rebuildExceptionValidators(data, props);
                }
            },
            {
                deep: true
            }
        );


        return {
            ...data,
            ...methods,
            'HoursOfOperationType': HoursOfOperationType,
            'dateDisplay': formatDate,
            'dateRangeDisplay': formatDateRange,
            'HoursOfOperationDisplayType': HoursOfOperationDisplayType
        }
    }
});

